import type {
  Rental,
  RentalAmenities,
  RentalLocation,
  RentalLockBridge,
  RentalMainImage,
  RentalTime,
  RentalType,
} from '@interfaces/rental.interface';

import {
  AMENITY_POOL,
  CITY_DATA,
  FIRST_NAMES,
  LAST_NAMES,
  RENTAL_ADJECTIVES,
  RENTAL_DESCRIPTIONS,
  RENTAL_DWELLING_TYPES,
  RENTAL_PLACES,
  RENTAL_SHORT_DESCRIPTIONS,
  RENTAL_TYPE_DATA,
  STREET_NAMES,
} from '../pools';
import { pick, pickN, randomBool, randomFloat, randomInt, randomPhone, seq, uuid } from '../utils';

function buildRentalType(): RentalType {
  const data = pick(RENTAL_TYPE_DATA);
  return { id: data.id, name: data.name };
}

function buildRentalTime(): RentalTime {
  const hours = [14, 15, 16];
  const outHours = [10, 11, 12];
  return {
    checkIn: { from: `${pick(hours)}:00`, to: `${pick(hours)}:00` },
    checkOut: { from: `${pick(outHours)}:00`, to: `${pick(outHours)}:00` },
  };
}

const CITY_COORDS: { lat: number; lng: number; zip: string }[] = [
  { lat: 40.4168, lng: -3.7038, zip: '28001' },
  { lat: 41.3825, lng: 2.1769, zip: '08001' },
  { lat: 39.4699, lng: -0.3763, zip: '46001' },
  { lat: 36.7201, lng: -4.4203, zip: '29001' },
  { lat: 37.1773, lng: -3.5986, zip: '18001' },
  { lat: 41.4518, lng: 2.2477, zip: '08820' },
  { lat: 37.3891, lng: -5.9845, zip: '41001' },
  { lat: 41.3598, lng: 2.1, zip: '08900' },
];

function buildRentalLocation(): RentalLocation {
  const cityIdx = randomInt(0, CITY_DATA.length - 1);
  const city = CITY_DATA[cityIdx];
  const coords = CITY_COORDS[cityIdx];
  const lat = coords.lat + randomFloat(-0.004, 0.004, 6);
  const lng = coords.lng + randomFloat(-0.006, 0.006, 6);
  const latStr = String(lat);
  const lngStr = String(lng);
  return {
    address: `${pick(STREET_NAMES)} ${randomInt(1, 200)}`,
    city: { id: city.id, name: city.name },
    country: { id: 'ES', name: 'España' },
    zipCode: coords.zip,
    marker: { latitude: latStr, longitude: lngStr },
    map: { latitude: latStr, longitude: lngStr },
  };
}

function buildRentalAmenities(): RentalAmenities {
  const result: Record<string, string[]> = {};
  for (const key of Object.keys(AMENITY_POOL)) {
    const pool = AMENITY_POOL[key];
    if (key === 'policies') {
      result[key] = pickN(pool.names, randomInt(2, pool.names.length));
    } else {
      result[key] = pickN(pool.names, randomInt(2, Math.min(4, pool.names.length)));
    }
  }
  return result as unknown as RentalAmenities;
}

function buildMainImage(): RentalMainImage {
  const id = uuid();
  return {
    id,
    url: `https://picsum.photos/seed/${id}/800/600`,
  };
}

function buildLockBridge(): RentalLockBridge {
  return {
    ragId: randomBool(0.6) ? uuid() : null,
    ragLoginUser: randomBool(0.5) ? `lock-user-${randomInt(1, 100)}` : null,
    ragLoginPass: null,
    ragMainDeviceId: randomBool(0.6) ? uuid() : null,
    canLock: false,
  };
}

export function buildRental(overrides?: Partial<Rental>): Rental {
  const title = `${pick(RENTAL_ADJECTIVES)} ${pick(RENTAL_PLACES)} ${pick(RENTAL_DWELLING_TYPES)}`;
  const isDraft = randomBool(0.2);
  const firstName = pick(FIRST_NAMES);
  const lastName = pick(LAST_NAMES);

  return {
    id: uuid(),
    externalCodeNumber: seq(),
    title,
    description: pick(RENTAL_DESCRIPTIONS),
    shortDescription: pick(RENTAL_SHORT_DESCRIPTIONS),
    defaultPrice: randomFloat(60, 500, 2),
    rating: randomFloat(3.0, 5.0, 1),
    maxGuests: randomInt(2, 12),
    instantBooking: randomBool(0.4),
    isDraft,
    hasPublished: !isDraft || randomBool(0.3),
    time: buildRentalTime(),
    rentalType: buildRentalType(),
    createdAt: new Date(Date.now() - randomInt(30, 730) * 86400000).toISOString(),
    amenities: buildRentalAmenities(),
    location: buildRentalLocation(),
    bathrooms: randomInt(1, 4),
    bedrooms: randomInt(1, 6),
    area: randomInt(40, 300),
    minStay: randomInt(0, 5),
    maxStay: randomBool(0.5) ? 0 : randomInt(7, 30),
    emergencyInfo: {
      police: '112',
      hospital: '112',
      fireStation: '112',
    },
    contactPerson: {
      id: uuid(),
      name: firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      phone: randomPhone(),
    },
    bookingCutOffDate: '1',
    rentalMirCode: `MIR-${randomInt(10000, 99999)}`,
    rentalNra: randomBool(0.5) ? `NRA-${randomInt(1000, 9999)}` : null,
    mainImage: buildMainImage(),
    hostId: undefined,
    lock: randomBool(0.6) ? buildLockBridge() : undefined,
    locationText: null,
    faqs: [],
    ...overrides,
  };
}

export function buildRentals(count: number): Rental[] {
  return Array.from({ length: count }, () => buildRental());
}
